"use client";

import React, { useEffect, useState } from "react";
import { Product } from "@/app/lib/marketplace/orders";

export default function MarketPurchaseSection({
  product,
}: {
  product: Product;
}) {
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "midtrans">(
    "stripe",
  );
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const DEFAULT_EXCHANGE_RATE = 17000;
  const [exchangeRate, setExchangeRate] = useState(DEFAULT_EXCHANGE_RATE);
  const whatsappNumber = "6282310268609";

  // Price calculations
  const usdPrice = product.price_cents / 100;
  const usdComparePrice = product.compare_at_price_cents
    ? product.compare_at_price_cents / 100
    : usdPrice / 0.1;

  useEffect(() => {
    let isActive = true;

    const loadExchangeRate = async () => {
      try {
        const res = await fetch("/api/exchange-rate?base=USD&symbols=IDR");
        if (!res.ok) return;
        const data = (await res.json()) as { rate?: number };
        if (typeof data.rate === "number" && data.rate > 0 && isActive) {
          setExchangeRate(data.rate);
        }
      } catch {
        // Keep fallback exchange rate on failure.
      }
    };

    loadExchangeRate();
    return () => {
      isActive = false;
    };
  }, []);

  const idrPrice = usdPrice * exchangeRate;
  const idrComparePrice = usdComparePrice * exchangeRate;

  const formatPrice = (amount: number, method: string) => {
    if (method === "midtrans") {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    }
    return `$${amount.toFixed(2)}`;
  };

  const methods = [
    {
      id: "stripe",
      label: "Stripe",
      icon: "credit_card",
      desc: "Temporarily disabled",
    },
    {
      id: "midtrans",
      label: "Midtrans",
      icon: "account_balance_wallet",
      desc: "Temporarily disabled",
    },
  ];

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerEmail || !customerName) return;

    setIsCheckingOut(true);

    try {
      const priceValue = paymentMethod === "midtrans" ? idrPrice : usdPrice;
      const currencyLabel = paymentMethod === "midtrans" ? "IDR" : "USD";
      const message = encodeURIComponent(
        [
          `Hello Shecraa Studio, I want to purchase: ${product.title}`,
          `Price: ${currencyLabel} ${priceValue.toFixed(paymentMethod === "midtrans" ? 0 : 2)}`,
          `Name: ${customerName}`,
          `Email: ${customerEmail}`,
          `Product ID: ${product.id}`,
          "Please assist me with the WhatsApp purchase flow.",
        ].join("\n")
      );

      window.location.href = `https://wa.me/${whatsappNumber}?text=${message}`;
    } catch (error) {
      console.error("Checkout error:", error);
      alert("Could not open WhatsApp. Please try again.");
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Price Header - Integrated */}
      <div className="space-y-2">
        <span className="font-label-sm text-[10px] uppercase tracking-[0.3em] text-secondary">
          Commercial License
        </span>
        <div className="flex items-baseline gap-3">
          <span className="font-bold text-[28px] md:text-[32px] tracking-tight leading-none text-primary transition-all duration-300">
            {formatPrice(paymentMethod === "midtrans" ? idrPrice : usdPrice, paymentMethod)}
          </span>
          <span className="text-sm md:text-base text-secondary line-through font-light transition-all duration-300">
            {formatPrice(paymentMethod === "midtrans" ? idrComparePrice : usdComparePrice, paymentMethod)}
          </span>
          <span className="bg-primary text-on-primary text-[10px] font-bold px-2 py-1 uppercase tracking-tighter self-center ml-2">
            90% OFF
          </span>
        </div>
      </div>

      <div className="pt-6 border-t border-outline-variant/30">
        {!showDetails ? (
          /* Step 1: Select Payment Method */
          <div className="space-y-6">
            <div className="space-y-4">
              <p className="text-[11px] uppercase tracking-[0.15em] text-secondary font-medium px-0.5">
                Payment Gateway Status
              </p>
              <div className="grid grid-cols-1 gap-2.5">
                {methods.map((m) => {
                  const active = paymentMethod === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setPaymentMethod(m.id as "stripe" | "midtrans")}
                      className={`relative flex items-center p-4 rounded-xl text-left gap-4 transition-all duration-200
                  ${
                    active
                      ? "border-2 border-primary bg-surface-container-low"
                      : "border border-outline-variant bg-white hover:border-outline"
                  }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${active ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-secondary'}`}>
                        <span className="material-symbols-outlined text-[20px]">{m.icon}</span>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-bold text-primary block uppercase tracking-wider">{m.label}</span>
                        <span className="text-[10px] text-secondary uppercase tracking-widest leading-none">{m.desc}</span>
                      </div>
                      {active && (
                         <span className="material-symbols-outlined text-primary text-[20px]">check_circle</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={() => setShowDetails(true)}
              className="w-full h-16 bg-primary text-on-primary rounded font-label-sm text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-on-surface transition-all duration-300 shadow-lg active:scale-[0.98]"
            >
              Continue to Details
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
          </div>
        ) : (
          /* Step 2: Enter Details */
          <form onSubmit={handleCheckout} className="space-y-8 animate-fade-in-up">
            <div className="flex items-center justify-between px-0.5 border-b border-outline-variant/30 pb-4">
               <div className="space-y-1">
                <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-primary">
                  License Details
                </h3>
                <p className="text-[9px] text-secondary uppercase tracking-widest">Digital product assignment</p>
               </div>
              <button 
                type="button"
                onClick={() => setShowDetails(false)}
                className="text-[9px] font-bold uppercase tracking-widest text-secondary hover:text-primary transition-colors py-1 px-3 border border-outline-variant rounded-full hover:border-primary"
              >
                Back
              </button>
            </div>
            
            <div className="space-y-5 bg-surface-container-lowest/50 p-4 rounded-2xl border border-outline-variant/20">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold px-1 flex justify-between">
                  <span>Full Name</span>
                  <span className="text-secondary/50 font-medium">Required</span>
                </label>
                <input 
                  required
                  type="text"
                  placeholder="E.G. MARCUS CHEN"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full h-14 bg-white border border-outline-variant rounded-xl px-5 text-sm text-primary placeholder:text-secondary/30 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all uppercase tracking-wider"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold px-1 flex justify-between">
                  <span>Email Address</span>
                  <span className="text-secondary/50 font-medium">Required</span>
                </label>
                <input 
                  required
                  type="email"
                  placeholder="MARCUS@EXAMPLE.COM"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="w-full h-14 bg-white border border-outline-variant rounded-xl px-5 text-sm text-primary placeholder:text-secondary/30 outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all uppercase tracking-wider"
                />
                <div className="flex items-start gap-2 px-1 pt-1">
                  <span className="material-symbols-outlined text-[14px] text-primary">info</span>
                  <p className="text-[9px] text-secondary uppercase tracking-tighter leading-tight">
                    The license key and high-performance download link will be delivered to this verified address.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={isCheckingOut}
                className="w-full h-16 bg-primary text-on-primary rounded font-label-sm text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-on-surface transition-all duration-300 disabled:opacity-40 shadow-lg active:scale-[0.98]"
              >
                {isCheckingOut ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white" />
                ) : (
                  <span className="material-symbols-outlined text-[18px]">chat</span>
                )}
                {isCheckingOut
                  ? "Opening WhatsApp..."
                  : `Order via WhatsApp`}
              </button>
              <p className="text-[8px] text-center text-secondary/40 uppercase tracking-[0.2em]">No gateway checkout is active yet • Order confirmation is handled on WhatsApp</p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
